import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import { t } from 'ttag';
import { ChannelConfigSettings } from '../../../../config/channels';
import { operatorName } from '../../../../config/operator';
import { TSite } from '../../../../contexts/site/site-context';
import { filterSites } from '../../../../services/api/site';
import SiteCard from './SiteCard';
import { trackSiteSelectSearch } from '../../../../services/siteSelectTracking';
import icon_search from '../../../../assets/images/search.svg';
import {useSearchParams} from "react-router-dom";
import useOperatorContext from "../../../../contexts/operator/useOperatorContext";

interface SiteFilterProps {
    sites: TSite[];
    channel: ChannelConfigSettings;
}

const SiteFilter = ({ sites = [], channel }: SiteFilterProps) => {
    const operatorContext = useOperatorContext();

    const [filteredSites, setFilteredSites] = useState<TSite[]>(sites ?? []);
    const [mappedSites, setMappedSites] = useState<TSite[]>(sites ?? []);
    const [filter, setFilter] = useState('');
    const [locationLoading, setLocationLoading] = useState(false);
    const [searchParams] = useSearchParams();

    const postcodeSearchInput = searchParams.has('postcode') ? searchParams.get('postcode') : null;

    // Reset search on channel change
    useEffect(() => {
        setFilter('');
    }, [channel]);

    useEffect(() => {
        if (!postcodeSearchInput) {
            return;
        }

        let mapped: TSite[] = [];

        filterSites(operatorName, postcodeSearchInput).then((data: { uid: string, distance: number }[]) => {

            mapped = sites.map((s) => {
                return {
                    ...s,
                    distance: data.find((site) => site.uid === s.uid )?.distance
                }
            }).sort((a,b) => (a.distance ?? 1) - (b.distance ?? 1) );

            setMappedSites(mapped);
            setFilteredSites(mapped);
            setLocationLoading(false);
        });
    }, [ postcodeSearchInput ]);

    // (searching is told of once typing pauses)
    useEffect(() => {
        if (filter.length < 3) {
            return;
        }

        const timer = setTimeout(() => trackSiteSelectSearch('page', channel.name, 'text'), 600);

        return () => clearTimeout(timer);
    }, [filter]);

    useEffect(() => {
        let filtered: TSite[] = [];

        if (filter.length > 2) {
            filterSites(operatorName, filter).then((data: {uid: string, distance?: number}[]) => {

                if (data.length > 0 && data[0].hasOwnProperty('distance')) {
                    filtered = sites.map((s) => {
                        return {
                            ...s,
                            distance: data.find((site) => site.uid === s.uid )?.distance
                        }
                    }).sort((a,b) => (a.distance ?? 1) - (b.distance ?? 1));
                } else {
                    filtered = sites.filter((site) => {
                        // Search by name
                        return data.findIndex(s => s.uid === site.uid) !== -1;
                    });
                }

                setFilteredSites(filtered);
            });
        } else {
            filtered = sites;

            if (postcodeSearchInput) {
                filtered = mappedSites;
            }

            setFilteredSites(filtered);
        }
    }, [filter]);

    return (
        <StyledForm nearest={(operatorContext.operator?.theme.v4.location_search ?? false).toString()}>
            { operatorContext.operator?.theme.v4.location_search ? (
                <InputWrapper input={filter} controlId={"formSearch"}>
                    <div className={'filter-input-wrapper'}>
                        <Form.Control
                            size="lg"
                            type="text"
                            className=""
                            placeholder={`${ filterSites.length > 0 ? `Search ${filteredSites.length} location${filteredSites.length > 1 ? 's' : ''} by city, street or postcode` : 'Search by city, street or postcode'}`}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value.trim())}
                        />
                        <ClearFormBtn
                            className={`${ filter.length > 0 ? 'with-input' : 'no-input' }`}
                            onClick={() => setFilter('')}>
                            <span className="material-symbols-outlined">close</span>
                        </ClearFormBtn>
                        <span className="material-symbols-outlined filter-input__search">search</span>
                    </div>
                </InputWrapper>
            ) : null }

            <div className="wrap-sites">
                {
                    filteredSites.map((site) => (
                        <SiteCard
                            site={site}
                            channel={channel}
                            key={`${channel.name}_${site.uid}`}
                        />
                    ))
                }
            </div>
        </StyledForm>
    );
};

const StyledForm = styled(Form.Group)<{ nearest: string }>`
    .invalid-alert {
        margin: 0 8px;
    }

    .wrap-sites {
        ${(props) => (props.nearest === 'true' ? `margin-top: 20px;` : `margin-top: 12px;`)}
        padding: 0 60px;
        @media screen and (max-width: 767px) {
            padding: 0;
        }
    }
`;

const ClearFormBtn = styled.button`
    border: 0;
    background-color: transparent;
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);

    color: var(--text-grey);
    font-size: 16px;
    line-height: 24px;

    align-items: center;

    img { margin-left: 8px; }

    &.with-input {display: flex; }
    &.no-input { display: none; }
`;

const InputWrapper = styled(Form.Group)<{ input: string }>`
    position: relative;
    padding: 0 60px;

    @media screen and (max-width: 767px) {
        padding: 0 16px;
    }

    .form-control {
        padding: 18px 16px 18px 48px;
        text-overflow: ellipsis;
        ${(props) => (props.input.length > 0 ? `padding-right: 48px;` : null)}
    }

    .filter-input-wrapper {
        position: relative;
    }

    .filter-input__search {
        position: absolute;
        top: 50%;
        left: 12px;
        transform: translateY(-50%);
        padding: 4px;
        border-radius: var(--radius_m);
        color: var(--text-black);
        font-size: 24px;
        line-height: 24px;
    }

    .form-label {
        font-size: var(--core_text_ui_lg);
        @media screen and (min-width: 576px) {
            font-size: var(--core_text_ui_lg_desktop);
        }
        line-height: 24px;
        color: var(--text-black);
        margin-bottom: 12px;
    }

    .location_icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 14px;
        cursor: pointer;
    }

    .spinner-grow {
        height: 18px;
        width: 18px;
    }
`;

export default SiteFilter;
